import {
  getAllPayrollsForEmployee,
  selectState,
  setCalculationPopoverOpen,
  setCalculationAllPayrollsConfirmationOpen,
  setCalculationPrintModalOpen,
} from "../payroll.slice";
import { useSelector } from "react-redux";
import { useAppDispatch } from "hooks/hooks";
import { ICalculatePayrollForRemainingEmployeesRequestParams } from "Dashboard/scenes/HumanResources/domain/ICalculatePayrollForRemainingEmployeesRequestParams";
import { useForm } from "react-hook-form";
import { hrService } from "Dashboard/scenes/HumanResources/services/hr.services";
import { toast } from "react-toastify";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { selectEmployee } from "../../employee/employeeSlice";
import MonthSelect from "Dashboard/components/MonthSelect";
import { IGetPayrollSummaryForEmployeeRequestParams } from "Dashboard/scenes/HumanResources/domain/IGetPayrollSummaryForEmployeeRequestParams";
import { IPayrollSummary } from "Dashboard/scenes/HumanResources/domain/IPayrollSummary";
import { FormatNumber } from "utils/numberUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import PrintIcon from "@mui/icons-material/Print";
import classes from "../../../../../../components/Popover/popover.module.scss";
import classesHr from "../../../popover.module.scss";
import Loader from "components/Loader/Loader";
import Modal from "components/Modal/Modal";
import { monthNames } from "utils/translationDictionary";
import LinearProgress from "@mui/material/LinearProgress";
import { PayrollCalculationStatus } from "Dashboard/scenes/HumanResources/domain/PayrollCalculationStatus";

interface IProps {
  month: number;
  year: number;
  availableYears: number[];
  setMonth: (month: number) => void;
  setYear: (year: number) => void;
}

const CalculateAllPayrollsPopover = ({
  month,
  year,
  availableYears,
  setMonth,
  setYear
}: IProps) => {
  const {
    calculationPopoverOpen,
    calculationAllPayrollsConfirmationOpen
  } = useSelector(selectState);
  const dispatch = useAppDispatch();
  const [summary, setSummary] = useState<IPayrollSummary>();
  const employee = useSelector(selectEmployee);
  const [monthToCalculate, setMonthToCalculate] = useState<number | undefined>(undefined);
  const [yearToCalculate, setYearToCalculate] = useState<number | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] =
    useState<{ processed: number; total: number } | undefined>(undefined);
  // Flipped on unmount so an in-flight poll loop stops touching state.
  const pollCancelledRef = useRef(false);

  useEffect(() => {
    return () => {
      pollCancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (month !== undefined && year !== undefined) {
      setMonthToCalculate((month + 11) % 12);
      setYearToCalculate(month > 0 ? year : year - 1);
    }
  }, [month, year]);

  useEffect(() => {
    const getSummary = async (
      data: IGetPayrollSummaryForEmployeeRequestParams
    ) => {
      try {
        setSummary(undefined);
        const summary = await hrService.getPayrollSummaryForEmployee(data);
        setSummary(summary);
      } catch (err) {
        console.log(err);
      }
    };

    if (monthToCalculate !== undefined && yearToCalculate !== undefined) {
      if (calculationPopoverOpen) {
        getSummary({ year: yearToCalculate, month: monthToCalculate + 1 });
      }
      setMonth(monthToCalculate + 1);
      setYear(yearToCalculate);
    }
  }, [monthToCalculate, yearToCalculate, calculationPopoverOpen]);


  const close = () => {
    dispatch(setCalculationPopoverOpen({ isOpen: false }));
    dispatch(setCalculationAllPayrollsConfirmationOpen(false));
  };

  const print = () => {
    dispatch(setCalculationPrintModalOpen(true));
  };

  const { handleSubmit, register, setFocus } =
    useForm<ICalculatePayrollForRemainingEmployeesRequestParams>();

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const submitPayrollsCalculation = async () => {
    if (submitting) return;
    if (yearToCalculate === undefined || monthToCalculate === undefined) return;
    try {
      setSubmitting(true);
      pollCancelledRef.current = false;

      const job = await hrService.startPayrollCalculationForRemainingEmployees({
        year: yearToCalculate,
        month: monthToCalculate + 1,
      });
      setProgress({ processed: 0, total: job.total });

      // Poll the background job until it finishes, updating the progress bar.
      while (!pollCancelledRef.current) {
        const status = await hrService.getPayrollCalculationStatus(job.jobId);
        setProgress({ processed: status.processed, total: status.total });

        if (status.status === PayrollCalculationStatus.DONE) {
          toast.success("Obračun uspešno kreiran za sve zaposlene");
          if (employee) {
            dispatch(getAllPayrollsForEmployee(employee.employeeId));
          }
          close();
          return;
        }
        if (status.status === PayrollCalculationStatus.FAILED) {
          toast.error("Obračun nije uspeo za sve zaposlene. Pokušajte ponovo.");
          return;
        }
        await sleep(1000);
      }
    } catch (error: any) {
      toast.error("Obračun nije uspeo za sve zaposlene. Pokušajte ponovo.");
    } finally {
      setSubmitting(false);
      setProgress(undefined);
    }
  }

  const onSubmit = async () => {
    dispatch(setCalculationAllPayrollsConfirmationOpen(true));
  };

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    let { name, value } = event.target;
    if (name === "year") {
      setYearToCalculate(Number(value));
    } else {
      setMonthToCalculate(Number(value) - 1);
    }
  };

  useEffect(() => {
    setFocus("month");
  }, [setFocus]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}
      className={`
        ${classesHr.calculatePopover}
        ${classes.basicPopoverView}
        ${classes.gPopoverInputs}`}>

      <Modal
        isOpen={calculationAllPayrollsConfirmationOpen && monthToCalculate !== undefined}
        onClick={submitPayrollsCalculation}
        onClose={() => {
          if (!submitting) dispatch(setCalculationAllPayrollsConfirmationOpen(false));
        }}
        title="Obračun plata"
        actionsDisabled={submitting}
      >
        {submitting && progress ? (
          <div className="info-msg">
            <p>
              Obračun u toku... {progress.processed} / {progress.total}
              <br />
              <br />
            </p>
            <LinearProgress
              variant={progress.total > 0 ? "determinate" : "indeterminate"}
              value={progress.total > 0
                ? Math.round((progress.processed / progress.total) * 100)
                : 0}
            />
          </div>
        ) : (
          monthToCalculate !== undefined && (
            <p className="info-msg">
              Da li ste sigurni da želite da otvorite platu za sve radnike za {monthNames[monthToCalculate].toLowerCase()} mesec?
              <br />
              <br />
            </p>
          )
        )}
      </Modal>
      <div>
        <div>
          <label>OBRAČUN<br /> za period</label>
          <div className={classesHr.calculationDiv}>
            {monthToCalculate !== undefined && yearToCalculate !== undefined && (
              <div>
                <select
                  {...register("year")} onChange={handleChange} disabled={summary === undefined} defaultValue={yearToCalculate}>
                  {availableYears.map((year, index) => (
                    <option value={year} key={index}>
                      {year}
                    </option>
                  ))}
                </select>
                <MonthSelect
                  onChange={handleChange}
                  defaultValue={monthToCalculate + 1}
                  disabled={summary === undefined}
                />
              </div>
            )}
            <div className={classes.buttons}>
              <button className={classes.iconButton} disabled={summary === undefined} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
              <button onClick={print} disabled={summary === undefined} className={classes.iconButton} type="button">
                <PrintIcon className={classes.icon} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {!summary &&
        <div className={classesHr.summary}>
          <div className={classesHr.loading}><Loader /></div>
        </div>
      }
      {summary && (
        <>
          <div className={classes.subInputs}>
            <div>
              <label>br. plata</label>
              <div>
                <input type="text" className={classes.dotField}
                  value={summary.numberOfSalaries} disabled />
              </div>
            </div>
          </div>
          <div className={classes.subInputs}>
            <div>
              <label>na račun</label>
              <div>
                <input type="text" className={classes.dotField}
                  value={FormatNumber(summary.toPayByBank) + " rsd"} disabled />
              </div>
            </div>
          </div>
          <div className={classes.subInputs}>
            <div>
              <label>prevoz</label>
              <div>
                <input type="text" className={classes.dotField}
                  value={FormatNumber(summary.travelExpenses) + " rsd"} disabled />
              </div>
            </div>
          </div>
          <div className={classes.subInputs}>
            <div>
              <label>na ruke</label>
              <div>
                <input type="text" className={classes.dotField}
                  value={FormatNumber(summary.toPayByHand) + " rsd"} disabled />
              </div>
            </div>
          </div>
          <div className={classes.subInputs}>
            <div>
              <label>keš obroci</label>
              <div>
                <input type="text" className={classes.dotField}
                  value={summary.cashMeals} disabled />
              </div>
            </div>
          </div>
          <div className={classes.subInputs}>
            <div>
              <label>bolovanja</label>
              <div>
                <input type="text" className={classes.dotField}
                  value={summary.sickLeaves} disabled />
              </div>
            </div>
          </div>
          <div className={classes.subInputs}>
            <div>
              <label>odmori</label>
              <div>
                <input type="text" className={classes.dotField}
                  value={summary.vacations} disabled />
              </div>
            </div>
          </div>
          <div className={classes.subInputs}>
            <div>
              <label>bonus</label>
              <div>
                <input type="text" className={classes.dotField}
                  value={FormatNumber(summary.bonuses) + " rsd"} disabled />
              </div>
            </div>
          </div>
          <div className={classes.subInputs}>
            <div>
              <label>korekcija</label>
              <div>
                <input type="text" className={classes.dotField}
                  value={FormatNumber(summary.corrections) + " rsd"} disabled />
              </div>
            </div>
          </div>
          < div className={classes.subInputs}>
            <div>
              <label>ispl / troš</label>
              <div>
                <input type="text" className={classes.dotField}
                  value={FormatNumber(summary.totalPaidExpenses) + " rsd"} disabled />
              </div>
            </div>
          </div>
        </>
      )
      }
    </form >
  );
};

export default CalculateAllPayrollsPopover;
